import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import Table from 'apollo-react/components/Table';
import Typography from 'apollo-react/components/Typography';
import Plus from 'apollo-react-icons/Plus';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  addTableRow,
  deleteTableRow,
  editTableRow,
  validateSavedSearch,
} from './../actions/editor.actions';
import { getSavedSearchTableRows } from './../selectors/editor.selectors';
import ActionCell from './table/ActionCell';
import EditableCell from './table/EditableCell';
import { useSavedSearchSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import {
  ACTION_TEMPLATE_DETAIL,
  ACTIONS_TEMPLATE,
  ACTIONS_URL,
  DEFAULT_URL_TEMPLATE_OPTIONS,
  MAX_ACTIONS_TABLE_ROWS_COUNT,
  SAVED_SEARCH_ACTION,
  SAVED_SEARCH_ACTION_TABLE_LABEL,
} from '../constants/editor.constants';
import { getColumnsWithTranslations } from '../utils/common.utils';

const useStyles = makeStyles({
  addButtonContainer: {
    textAlign: 'right',
    paddingTop: '8px !important',
    paddingRight: '15px !important',
  },
  controlWrapper: {
    position: 'relative',
  },
  select: {
    margin: 3,
    paddingRight: 3,
  },
});

const ActionTemplateCell = () => {
  const translateString = useTranslation();
  return <Typography>{translateString('Navigation URL')}</Typography>;
};

const columns = [
  {
    header: 'Action Template',
    accessor: ACTIONS_TEMPLATE,
    customCell: ActionTemplateCell,
    width: 300,
  },
  {
    header: 'Action Url',
    accessor: ACTIONS_URL,
    customCell: EditableCell,
    enableLookup: true,
    lookupValues: DEFAULT_URL_TEMPLATE_OPTIONS,
    enableLookupCopy: true,
    required: true,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
    width: 55,
  },
];

export const actionsTableValidationProps = [
  {
    prop: ACTIONS_URL,
    errorLabel: 'Action Url',
    required: true,
    tableName: SAVED_SEARCH_ACTION_TABLE_LABEL,
  },
];

const ActionsTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const rows = useSavedSearchSelector(
    getSavedSearchTableRows,
    { tablePath: SAVED_SEARCH_ACTION },
    equalityFn
  );

  const columnsWithTranslations = useMemo(() => {
    return getColumnsWithTranslations({ columns, translateString });
  }, [translateString]);

  const addRow = () => {
    const newRow = {
      rowId: uuidv4(),
      actionTemplate: ACTION_TEMPLATE_DETAIL,
      actionName: ACTION_TEMPLATE_DETAIL,
      url: '',
    };

    dispatch(addTableRow({ tableName: SAVED_SEARCH_ACTION, newRow }));
  };

  const editRow = (params) => {
    dispatch(editTableRow({ ...params, tableName: SAVED_SEARCH_ACTION }));
    params.error && dispatch(validateSavedSearch());
  };

  const deleteRow = (params) => {
    dispatch(deleteTableRow({ ...params, tableName: SAVED_SEARCH_ACTION }));
  };

  return (
    <Grid container spacing={1}>
      <Grid classes={{ item: classes.addButtonContainer }} item xs={12}>
        <span>
          <Button
            color="primary"
            variant="secondary"
            onClick={addRow}
            disabled={rows.length >= MAX_ACTIONS_TABLE_ROWS_COUNT}
            icon={Plus}
            iconPosition="start"
            size="small"
          >
            {translateString('Add')}
          </Button>
        </span>
      </Grid>
      <Grid style={{ overflowX: 'auto' }} item xs={12}>
        <Table
          columns={columnsWithTranslations}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          rowId="rowId"
          rows={rows.map((row) => ({
            ...row,
            classes,
            editRow,
            deleteRow,
            translateString,
          }))}
          hidePagination
        />
      </Grid>
    </Grid>
  );
};

export default ActionsTable;
